import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import Modal from 'react-modal';
import classes from '../../../Timetables/DefaultTimetable/DefaultTimetable/SingleDay.module.scss';
import CustomButton from '../../../../components/CustomButton/CustomButton';
import Loading from '../../../../components/Loading/Loading';
import withUser from '../../../../store/hoc/withUser';
import { returnClassConfig } from '../../../../shared/gymFunctions';
import { returnClassesDetails } from '../../../../shared/classFunctions';
import { delayFetchFunctions, signUpAndPayForClass } from '../../../../apiFunctions/apiFunctions';
import { useMutation } from 'react-query';
import CustomToast from '../../../../components/CustomToast/CustomToast';
import { useStripe } from '@stripe/react-stripe-js';

const SignUpAndPayButton = ({ history, userData, sourcesList, selectedClass, selectedGym }) => {
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [userHasClassesLeftToUse, setUserHasClassesLeftToUse] = useState(false);
  const classesDetails = returnClassesDetails(selectedClass.__t);
  const config = returnClassConfig(selectedGym.settings.classConfig, selectedClass.__t);
  const isFreeEvent = selectedClass.cost === 0;
  const isClassMembershipUser = userData.customerType === 'full-member' && selectedClass.__t === 'class';
  const stripe = useStripe();

  useEffect(() => {
    setUserHasClassesLeftToUse(
      userData[classesDetails.usersClassesLeft] > 0 ||
        (userData.customerType === 'full-member' &&
          userData.currentMembershipPlan &&
          userData.currentMembershipPlan.ssPlanId &&
          userData.currentMembershipPlan.ssPlanId.unlimited &&
          selectedClass.__t === 'class')
    );
  }, [userData, classesDetails, selectedClass]);

  const mutateSignUpAndPay = useMutation(
    ({ isSinglePayment }) => signUpAndPayForClass(selectedClass._id, userData._id, isSinglePayment),
    {
      onSuccess: async ({ status, clientSecret }) => {
        if (status === 'requires_action') {
          const result = await stripe.confirmCardPayment(clientSecret, {});

          if (result.error) {
            return setToast({
              status: 'critical',
              msg: result.error?.message,
              show: true
            });
          }
        }

        setToast({
          status: 'ok',
          msg: isFreeEvent || userHasClassesLeftToUse ? 'Booking successful.' : 'Successfully added to this class',
          show: true
        });
        setShowModal(false);

        delayFetchFunctions([
          ['upcomingClasses', selectedClass.__t],
          ['clientUpcomingClasses', selectedClass.__t],
          ['userData']
        ]);
      },
      onError: (err) => {
        setShowModal(false);
        setToast({
          status: 'critical',
          msg:
            err.response && err.response.status === 403
              ? 'Please activate your membership plan'
              : err.response && err.response.status === 422
              ? err.response.data.message
              : 'Something went wrong. Try again',
          show: true
        });
      }
    }
  );

  const renderPaymentUpModal = () => {
    const needsAddPaymentMethod = sourcesList && sourcesList.length === 0 && selectedClass.cost > 0;
    const closeModal = (
      <CustomButton
        style={{
          fontWeight: 700,
          backgroundColor: '#414142',
          margin: '15px auto',
          width: '100%'
        }}
        box
        dataCy="cancel_btn"
        label="Cancel"
        secondary
        onClick={() => setShowModal(false)}
      />
    );
    return (
      <Modal
        appElement={document.getElementById('root')}
        onRequestClose={() => setShowModal(false)}
        isOpen={showModal}
        className="ss-modal">
        <div className="grommet" style={{ marginTop: '15px' }} data-cy="class_booking_modal">
          <span className={classes.modalMsg}>
            {!isFreeEvent && config.allowedToBuyPasses && !userHasClassesLeftToUse
              ? isClassMembershipUser
                ? 'Do you want to pay for single one?'
                : 'Pay for this single one or buy a block?'
              : 'Confirm booking'}
          </span>
          {mutateSignUpAndPay.isLoading ? (
            <div style={{ textAlign: 'center' }}>
              <Loading size="large" />
            </div>
          ) : (
            <div className="ss-modal__buttons-container" style={{ display: 'block' }}>
              {isFreeEvent || userHasClassesLeftToUse ? (
                <CustomButton
                  primary
                  label="Book"
                  dataCy="book_btn"
                  secondary
                  className={classes.modalButtons}
                  onClick={() => mutateSignUpAndPay.mutate({ isSinglePayment: true })}
                />
              ) : (
                <CustomButton
                  className={classes.modalButtons}
                  primary
                  dataCy="pay_for_single_btn"
                  label="Pay for single"
                  secondary
                  onClick={() =>
                    needsAddPaymentMethod
                      ? history.push(`/gym/payment-methods`)
                      : mutateSignUpAndPay.mutate({
                          isSinglePayment: true,
                          userId: userData._id,
                          classId: selectedClass._id
                        })
                  }
                />
              )}
              {config.allowedToBuyPasses && !userHasClassesLeftToUse && !isFreeEvent && !isClassMembershipUser && (
                <CustomButton
                  label="Buy a block"
                  primary
                  dataCy="buy_a_block_btn"
                  className={classes.modalButtons}
                  onClick={() =>
                    history.push({
                      pathname: `/gym/${classesDetails.passesUrl}`,
                      state: { goBackToPreviousPage: true }
                    })
                  }
                />
              )}

              {closeModal}
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div>
      <CustomToast toast={toast} />
      {renderPaymentUpModal()}
      <CustomButton
        style={{ width: '100%', maxWidth: '100%', whiteSpace: 'nowrap' }}
        label="Sign up"
        secondary
        onClick={() => setShowModal(true)}
        dataCy="sign_up_btn"
      />
    </div>
  );
};

export default withRouter(withUser(SignUpAndPayButton));
